import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';

export function Navbar({ pages = [] }) {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const checkSession = () => {
      const sessionToken = localStorage.getItem('session_token');
      const userEmail = localStorage.getItem('user_email');
      const role = localStorage.getItem('user_role');
      
      if (sessionToken && userEmail) {
        setIsLoggedIn(true);
        setUser({ email: userEmail });
        setUserRole(role);
      } else {
        setIsLoggedIn(false);
        setUser(null);
        setUserRole(null);
      }
    };

    checkSession();

    // Listen for storage changes (handles login from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'session_token' || e.key === 'user_email' || e.key === 'user_role') {
        checkSession();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');

      // Call logout API to invalidate session on server
      if (sessionToken) {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken,
          },
          body: JSON.stringify({ sessionToken }),
        }).catch(err => console.error('Logout API error:', err));
      }

      // Clear all localStorage data
      localStorage.clear();

      // Clear all sessionStorage data
      sessionStorage.clear();

      // Clear browser cache
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (cacheErr) {
          console.warn('Cache clear warning:', cacheErr);
        }
      }

      // Reset state
      setIsLoggedIn(false);
      setUser(null);
      setUserRole(null);
      setNavOpen(false);

      // Redirect to login
      navigate('/login?logged_out=true');
    } catch (err) {
      console.error('Logout error:', err);
      // Force logout even if API call fails
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    }
  };

  const closeNav = () => {
    setNavOpen(false);
  };

  // Build navigation excluding login/signup if they're not logged in (handled separately below)
  let navigation = pages.length > 0 ? pages.filter(p => p.slug !== 'login' && p.slug !== 'signup') : [
    { slug: 'home', title: 'Home', icon: '🏠' },
    { slug: 'about', title: 'About Us', icon: 'ℹ️' },
    { slug: 'gallery', title: 'Gallery', icon: '📸' },
    { slug: 'contact', title: 'Contact', icon: '📧' },
    { slug: 'leaderboard', title: 'Leaderboard', icon: '🏆' },
  ];
  if (!navigation.find(p => p.slug === 'gallery')) {
    navigation.splice(2, 0, { slug: 'gallery', title: 'Gallery', icon: '📸' });
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ zIndex: 1050 }}>
      <div className="container-fluid">
        <a className="navbar-brand fw-bold d-flex align-items-center gap-2" href="/" style={{ fontSize: '1.6rem', transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
          <img src="/images/alo_laba_logo.png" alt="Alo@laba" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
          <span style={{ letterSpacing: '1px', fontWeight: 700, textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>Alo@laba</span>
        </a>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded={navOpen}
          aria-label="Toggle navigation"
          onClick={() => setNavOpen(!navOpen)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className={`collapse navbar-collapse ${navOpen ? 'show' : ''}`} id="navbarNav">
          <ul className="navbar-nav ms-auto">
            {navigation.map((page) => (
              <li className="nav-item" key={page.slug}>
                <a
                  className="nav-link"
                  href={page.slug === 'home' ? '/' : `/${page.slug}`}
                  onClick={closeNav}
                >
                  {page.icon} {page.title}
                </a>
              </li>
            ))}
            <li className="nav-item me-2 d-flex align-items-center ms-lg-3 my-2 my-lg-0">
              <a 
                className="btn btn-warning btn-sm fw-bold shadow-sm rounded-pill px-3 d-flex align-items-center gap-1 text-dark" 
                href="/donate" 
                onClick={closeNav}
                style={{ 
                  background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', 
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(253, 160, 133, 0.4)' 
                }}
              >
                💝 Donate
              </a>
            </li>
            <LanguageSwitcher />
            {isLoggedIn ? (
              <>
                <li className="nav-item">
                  <a className="nav-link" href="/my-schedule" onClick={closeNav}>
                    📅 My Schedule
                  </a>
                </li>
                {userRole === 'admin' ? (
                  <li className="nav-item">
                    <a className="nav-link" href="/admin-dashboard" onClick={closeNav}>
                      👨‍💼 Admin Dashboard
                    </a>
                  </li>
                ) : (
                  <li className="nav-item">
                    <a className="nav-link" href="/user-dashboard" onClick={closeNav}>
                      👤 My Dashboard
                    </a>
                  </li>
                )}
                <li className="nav-item dropdown">
                  <a
                    className="nav-link dropdown-toggle"
                    href="#"
                    id="userDropdown"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    ⚙️ Menu
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                    <li>
                      <span className="dropdown-item-text" style={{ fontSize: '0.85rem', color: '#666' }}>
                        {user?.email}
                      </span>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={() => {
                          handleLogout();
                          closeNav();
                        }}
                        style={{ cursor: 'pointer', border: 'none', background: 'none', textAlign: 'left', width: '100%', padding: '0.5rem 1rem' }}
                      >
                        🚪 Logout
                      </button>
                    </li>
                  </ul>
                </li>
              </>
            ) : (
              <>
                <li className="nav-item">
                  <a className="nav-link" href="/login" onClick={closeNav}>
                    🔐 Login
                  </a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="/signup" onClick={closeNav}>
                    ✍️ Sign Up
                  </a>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
